import React, { useState } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface CustomerCenterPageProps {
  onBack: () => void;
  onMenuClick?: (menu: string) => void;
}

export const CustomerCenterPage: React.FC<CustomerCenterPageProps> = ({ onBack, onMenuClick }) => {
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  // FAQ 데이터
  const faqs = [
    {
      id: "faq-1",
      question: "복약 기록을 어떻게 하나요?",
      answer: "홈 화면에서 '💊 약먹기' 탭을 선택하고, 약 이름과 시간을 선택한 후 '복용완료'를 눌러주시면 됩니다.",
    },
    {
      id: "faq-2",
      question: "혈당·혈압을 기록하는 방법은?",
      answer: "홈 화면에서 '🩺 측정' 탭을 선택하고, 측정한 혈당/혈압 수치를 입력하면 자동으로 저장됩니다.",
    },
    {
      id: "faq-3",
      question: "포인트는 어디에 사용하나요?",
      answer: "설정 페이지에서 '내 포인트'를 확인하실 수 있으며, 향후 건강용품 구매나 프리미엄 기능 이용 시 사용할 수 있습니다.",
    },
    {
      id: "faq-4",
      question: "건강 점수는 어떻게 계산되나요?",
      answer: "복약 준수율, 혈당·혈압 관리 수준, 일일 걸음수, 식단 균형을 종합적으로 평가하여 계산됩니다.",
    },
    {
      id: "faq-5",
      question: "데이터 삭제 또는 개인정보 관리는?",
      answer: "설정 페이지의 '환경설정'에서 계정 설정, 개인정보 수정, 데이터 관리 옵션을 찾을 수 있습니다.",
    },
  ];

  const toggleFaq = (id: string) => {
    setExpandedFaqId(expandedFaqId === id ? null : id);
  };

  const openKakaoChannel = () => {
    // 카카오 채널 링크 오픈
    window.open("https://pf.kakao.com/_/herbotai", "_blank");
  };

  const handleRequestConsultation = () => {
    if (onMenuClick) {
      onMenuClick("consultation-request");
    }
  };

  const handleMyInquiries = () => {
    if (onMenuClick) {
      onMenuClick("my-inquiries");
    }
  };

  return (
    <SimpleModulePage 
      title="고객센터" 
      subtitle="(상담 & 지원)" 
      icon="💬" 
      onBack={onBack}
    >
      {/* FAQ 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <span className="text-[18px]">❓</span>
          <span>FAQ</span>
        </h2>
        <SectionCard>
          <div className="space-y-2">
            {faqs.map((faq) => (
              <button
                key={faq.id}
                onClick={() => toggleFaq(faq.id)}
                className="w-full text-left transition-all duration-200"
              >
                <div className="flex items-start gap-3 py-3 px-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <span className="text-[16px] flex-shrink-0 mt-0.5">
                    {expandedFaqId === faq.id ? "▼" : "▶"}
                  </span>
                  <div className="flex-1">
                    <p className="text-[13px] font-semibold text-slate-900">{faq.question}</p>
                    {expandedFaqId === faq.id && (
                      <p className="mt-2 text-[12px] text-slate-600 leading-relaxed">{faq.answer}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* 상담원 연결 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <span className="text-[18px]">👨‍💼</span>
          <span>상담원 연결</span>
        </h2>
        <SectionCard>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="text-center">
              <p className="text-[12px] text-slate-600 mb-1">상담 가능 시간</p>
              <p className="text-[13px] font-semibold text-slate-900">오전 9시 ~ 오후 6시</p>
              <p className="text-[11px] text-slate-500 mt-0.5">평일 (토·일·공휴일 제외)</p>
            </div>
            <button
              onClick={handleRequestConsultation}
              className="w-full h-11 rounded-xl bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 transition-colors active:bg-violet-800"
            >
              상담 예약하기
            </button>
          </div>
        </SectionCard>
      </div>

      {/* 카카오 채널 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <span className="text-[18px]">💬</span>
          <span>카카오 채널</span>
        </h2>
        <SectionCard>
          <div className="flex flex-col items-center gap-2">
            <p className="text-[12px] text-slate-600 text-center">
              카카오톡 채널에서 언제든지 문의할 수 있습니다.
            </p>
            <button
              onClick={openKakaoChannel}
              className="w-full h-11 rounded-xl bg-yellow-300 text-slate-900 text-[13px] font-semibold hover:bg-yellow-400 transition-colors active:bg-yellow-500 flex items-center justify-center gap-2"
            >
              <span className="text-[16px]">💛</span>
              <span>카카오 채널 문의하기</span>
            </button>
          </div>
        </SectionCard>
      </div>

      {/* 나의 문의 내역 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <span className="text-[18px]">📋</span>
          <span>나의 문의</span>
        </h2>
        <SectionCard>
          <button
            onClick={handleMyInquiries}
            className="w-full py-3 px-3 text-left text-[13px] text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors active:bg-slate-100 flex items-center justify-between"
          >
            <span>내 문의 내역 확인</span>
            <span className="text-slate-400">→</span>
          </button>
        </SectionCard>
      </div>

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">서비스 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              건강 관련 긴급 상황이 발생하면 즉시 119로 신고하시거나 가까운 응급실을 방문하시기 바랍니다.
            </p>
          </div>
        </div>
      </SectionCard>
    </SimpleModulePage>
  );
};
